import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const SessionDetailPanel = ({ 
  session = null, 
  onClose = () => {},
  onResetSession = () => {},
  onRemoveParticipant = () => {},
  onAdjustBill = () => {},
  onSendReminder = () => {}
}) => {
  const [activeTab, setActiveTab] = useState('participants');

  if (!session) {
    return (
      <div className="bg-surface border border-border rounded-lg p-8 text-center">
        <Icon name="FileText" size={32} className="mx-auto text-muted-foreground mb-2" />
        <p className="text-text-secondary">Selecciona una sesión para ver los detalles</p>
      </div>
    );
  }

  const mockParticipants = [
    {
      id: '1',
      name: 'María García',
      email: 'maria.garcia@email.com',
      status: 'completed',
      itemsSelected: 3,
      totalItems: 3,
      amountToPay: 24.50,
      paidAmount: 24.50,
      joinedAt: '2025-11-14T19:30:00',
      lastActivity: '2025-11-14T20:15:00'
    },
    {
      id: '2',
      name: 'Carlos López',
      email: 'carlos.lopez@email.com',
      status: 'selecting',
      itemsSelected: 2,
      totalItems: 4,
      amountToPay: 32.75,
      paidAmount: 0,
      joinedAt: '2025-11-14T19:32:00',
      lastActivity: '2025-11-14T20:28:00'
    },
    {
      id: '3',
      name: 'Ana Martín',
      email: 'ana.martin@email.com',
      status: 'payment-pending',
      itemsSelected: 4,
      totalItems: 4,
      amountToPay: 28.90,
      paidAmount: 0,
      joinedAt: '2025-11-14T19:35:00',
      lastActivity: '2025-11-14T20:25:00'
    }
  ];

  const mockItems = [
    {
      id: 'item-1',
      name: 'Paella Valenciana',
      price: 18.50,
      quantity: 1,
      selectedBy: ['María García'],
      status: 'assigned'
    },
    {
      id: 'item-2',
      name: 'Gazpacho Andaluz',
      price: 6.00,
      quantity: 2,
      selectedBy: ['María García', 'Carlos López'],
      status: 'assigned'
    },
    {
      id: 'item-3',
      name: 'Jamón Ibérico',
      price: 24.00,
      quantity: 1,
      selectedBy: ['Carlos López', 'Ana Martín'],
      status: 'disputed'
    },
    {
      id: 'item-4',
      name: 'Sangría (1L)',
      price: 12.00,
      quantity: 1,
      selectedBy: [],
      status: 'unassigned'
    }
  ];

  const getParticipantStatusColor = (status) => {
    switch (status) {
      case 'completed':
        return 'bg-success text-success-foreground';
      case 'selecting':
        return 'bg-warning text-warning-foreground';
      case 'payment-pending':
        return 'bg-primary text-primary-foreground';
      case 'disconnected':
        return 'bg-error text-error-foreground';
      default:
        return 'bg-muted text-muted-foreground';
    }
  };

  const getItemStatusColor = (status) => {
    switch (status) {
      case 'assigned':
        return 'bg-success text-success-foreground';
      case 'disputed':
        return 'bg-error text-error-foreground';
      case 'unassigned':
        return 'bg-warning text-warning-foreground';
      default:
        return 'bg-muted text-muted-foreground';
    }
  };

  const formatAmount = (amount) => {
    return new Intl.NumberFormat('es-ES', {
      style: 'currency',
      currency: 'EUR'
    })?.format(amount);
  };

  const formatTime = (dateString) => {
    return new Date(dateString)?.toLocaleTimeString('es-ES', { 
      hour: '2-digit', 
      minute: '2-digit' 
    });
  };

  const getInitials = (name) => {
    return name?.split(' ')?.map(n => n?.[0])?.join('')?.toUpperCase()?.slice(0, 2);
  };

  const tabs = [
    { id: 'participants', label: 'Participantes', icon: 'Users' },
    { id: 'items', label: 'Artículos', icon: 'ShoppingCart' },
    { id: 'payments', label: 'Pagos', icon: 'CreditCard' },
    { id: 'activity', label: 'Actividad', icon: 'Activity' }
  ];

  return (
    <div className="bg-surface border border-border rounded-lg overflow-hidden">
      {/* Header */}
      <div className="p-4 border-b border-border bg-muted/30">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-lg font-semibold text-foreground">
              Mesa {session?.tableNumber} - {session?.tableName}
            </h3>
            <p className="text-sm text-text-secondary">
              ID: {session?.id} • Iniciada: {formatTime(session?.startTime)}
            </p>
          </div>
          <Button
            variant="ghost"
            size="icon"
            onClick={onClose}
            iconName="X"
            iconSize={16}
          />
        </div>
      </div>
      {/* Tabs */}
      <div className="border-b border-border">
        <div className="flex overflow-x-auto">
          {tabs?.map((tab) => (
            <button
              key={tab?.id}
              onClick={() => setActiveTab(tab?.id)}
              className={`
                flex items-center space-x-2 px-4 py-3 text-sm font-medium border-b-2 transition-colors whitespace-nowrap
                ${activeTab === tab?.id 
                  ? 'border-primary text-primary bg-primary/5' :'border-transparent text-text-secondary hover:text-foreground hover:border-border'
                }
              `}
            >
              <Icon name={tab?.icon} size={16} />
              <span>{tab?.label}</span>
            </button>
          ))}
        </div>
      </div>
      {/* Content */}
      <div className="p-4 max-h-96 overflow-y-auto">
        {activeTab === 'participants' && (
          <div className="space-y-3">
            {mockParticipants?.map((participant) => (
              <div key={participant?.id} className="flex items-center justify-between p-3 border border-border rounded-lg">
                <div className="flex items-center space-x-3">
                  <div className="w-10 h-10 bg-primary text-primary-foreground rounded-full flex items-center justify-center text-sm font-medium">
                    {getInitials(participant?.name)}
                  </div>
                  <div>
                    <div className="font-medium text-foreground">{participant?.name}</div>
                    <div className="text-sm text-text-secondary">{participant?.email}</div>
                    <div className="flex items-center space-x-2 mt-1">
                      <span className={`text-xs px-2 py-0.5 rounded-full ${getParticipantStatusColor(participant?.status)}`}>
                        {participant?.status === 'completed' ? 'Completado' :
                         participant?.status === 'selecting' ? 'Seleccionando' :
                         participant?.status === 'payment-pending' ? 'Pago Pendiente' : 'Desconectado'}
                      </span>
                      <span className="text-xs text-text-secondary">
                        {participant?.itemsSelected}/{participant?.totalItems} artículos
                      </span>
                    </div>
                  </div>
                </div>
                <div className="text-right">
                  <div className="font-mono font-medium text-foreground">
                    {formatAmount(participant?.amountToPay)}
                  </div>
                  {participant?.paidAmount > 0 && (
                    <div className="text-xs text-success">
                      {formatAmount(participant?.paidAmount)} pagado
                    </div>
                  )}
                  <div className="flex items-center space-x-1 mt-2">
                    <Button
                      variant="ghost"
                      size="xs"
                      onClick={() => onSendReminder(participant?.id)}
                      iconName="Bell"
                      iconSize={12}
                    >
                      Recordar
                    </Button>
                    <Button
                      variant="ghost"
                      size="xs"
                      onClick={() => onRemoveParticipant(participant?.id)}
                      iconName="UserMinus"
                      iconSize={12}
                    >
                      Remover
                    </Button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {activeTab === 'items' && (
          <div className="space-y-3">
            {mockItems?.map((item) => (
              <div key={item?.id} className="p-3 border border-border rounded-lg">
                <div className="flex items-center justify-between mb-2">
                  <div>
                    <div className="font-medium text-foreground">{item?.name}</div>
                    <div className="text-sm text-text-secondary">
                      Cantidad: {item?.quantity} • {formatAmount(item?.price)}
                    </div>
                  </div>
                  <span className={`text-xs px-2 py-1 rounded-full ${getItemStatusColor(item?.status)}`}>
                    {item?.status === 'assigned' ? 'Asignado' :
                     item?.status === 'disputed' ? 'En Disputa' : 'Sin Asignar'}
                  </span>
                </div>
                {item?.selectedBy?.length > 0 && (
                  <div className="text-sm text-text-secondary">
                    Seleccionado por: {item?.selectedBy?.join(', ')}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

        {activeTab === 'payments' && (
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="p-3 bg-muted/30 rounded-lg">
                <div className="text-sm text-text-secondary">Total de la Cuenta</div>
                <div className="text-lg font-mono font-semibold text-foreground">
                  {formatAmount(session?.totalAmount)}
                </div>
              </div>
              <div className="p-3 bg-success/10 rounded-lg">
                <div className="text-sm text-text-secondary">Pagado</div>
                <div className="text-lg font-mono font-semibold text-success">
                  {formatAmount(session?.paidAmount || 0)}
                </div>
              </div>
            </div>
            
            <div className="space-y-2">
              <h4 className="font-medium text-foreground">Estado de Pagos</h4>
              {mockParticipants?.map((participant) => (
                <div key={participant?.id} className="flex items-center justify-between p-2 border border-border rounded">
                  <span className="text-sm text-foreground">{participant?.name}</span>
                  <div className="flex items-center space-x-2">
                    <span className="text-sm font-mono">
                      {formatAmount(participant?.paidAmount)} / {formatAmount(participant?.amountToPay)}
                    </span>
                    {participant?.paidAmount >= participant?.amountToPay ? (
                      <Icon name="CheckCircle" size={16} className="text-success" />
                    ) : (
                      <Icon name="Clock" size={16} className="text-warning" />
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {activeTab === 'activity' && (
          <div className="space-y-3">
            {[
              { time: '20:28', user: 'Carlos López', action: 'Seleccionó Gazpacho Andaluz', type: 'selection' },
              { time: '20:25', user: 'Ana Martín', action: 'Completó selección de artículos', type: 'completion' },
              { time: '20:15', user: 'María García', action: 'Realizó pago de €24.50', type: 'payment' },
              { time: '20:10', user: 'Sistema', action: 'Recordatorio enviado a Carlos López', type: 'system' },
              { time: '19:35', user: 'Ana Martín', action: 'Se unió a la sesión', type: 'join' }
            ]?.map((activity, index) => (
              <div key={index} className="flex items-start space-x-3 p-2">
                <div className="w-2 h-2 bg-primary rounded-full mt-2" />
                <div className="flex-1">
                  <div className="text-sm text-foreground">{activity?.action}</div>
                  <div className="text-xs text-text-secondary">
                    {activity?.user} • {activity?.time}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
      {/* Actions */}
      <div className="p-4 border-t border-border bg-muted/30">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => onResetSession(session?.id)}
              iconName="RotateCcw"
              iconSize={14}
            >
              Reiniciar
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => onAdjustBill(session?.id)}
              iconName="Edit"
              iconSize={14}
            >
              Ajustar Cuenta
            </Button>
          </div>
          
          <div className="text-sm text-text-secondary">
            Última actividad: {formatTime(session?.lastActivity || session?.startTime)}
          </div>
        </div>
      </div>
    </div>
  );
};

export default SessionDetailPanel;